import React from 'react';
import { BackButton } from '~/components/BackButton';
import { BSOverlay } from '~/components/Bootstrap';
import { Card } from '~/components/Card';
import { LitePlanIndicator, SrfIndicator, RiskBandElevation } from '~/components/Patient';
import { ServicesContext } from '~/services';
import { formatDate, formatAddress } from '~/util';
import { Popover } from 'react-bootstrap';
import { PatientPrintSummary } from './PatientPrintSummary';

interface PatientInfoProps {
    patient: PatientType;
}
export function PatientInfo({ patient }: PatientInfoProps) {
    const services = React.useContext(ServicesContext);
    return (
        <Card
            title={
                <div className="row">
                    <div className="col">
                        <BackButton />
                        {`${patient.first_name} ${patient.last_name}`}
                        <BSOverlay
                            delay={{ show: 1500, hide: 0 }}
                            placement="top"
                            overlay={
                                <Popover>
                                    <Popover.Body>
                                        {patient.addresses.length === 0 ? (
                                            <span>Address is not available for this patient.</span>
                                        ) : (
                                            <>
                                                {formatAddress(patient.addresses[0]).map((line) => (
                                                    <div key={line}>{line}</div>
                                                ))}
                                            </>
                                        )}
                                        {patient.markets.length > 0 && (
                                            <div className="mt-2">
                                                Market: {patient.markets[0].name}
                                            </div>
                                        )}
                                        <div className="mt-2">GMPI: {patient.gmpi}</div>
                                    </Popover.Body>
                                </Popover>
                            }
                        >
                            <i className="fa-solid fa-md fa-location-dot text-end ms-3">
                                <span className="sr-only">Patient Address</span>
                            </i>
                        </BSOverlay>
                    </div>
                    <div className="col text-end">
                        {services.user.patientPrintingEnabled() && (
                            <PatientPrintSummary patientId={patient.id} />
                        )}
                    </div>
                </div>
            }
            className="mb-3"
        >
            <div className="row">
                <div className="col-md-3 col-6">
                    <span className="fw-bold">DOB: </span>
                    {formatDate(patient.dob)}
                </div>
                <div className="col-md-3 col-6">
                    <span className="fw-bold">Phone: </span>
                    {patient.phones[0]?.number}
                </div>
                <div className="col-md-3 col-6">
                    <span className="fw-bold">Member ID: </span>
                    {patient.insurance_plans?.[0]?.insco_id}
                    <SrfIndicator patient={patient} />
                </div>
                <div className="col-md-3 col-6">
                    <span className="fw-bold">Risk Band: </span>
                    {patient.patient_record.risk_band_level === null
                        ? '--'
                        : patient.patient_record.risk_band_level}
                    <RiskBandElevation metadata={patient.metadata} />
                </div>
            </div>
            <div className="row">
                <div className="col-md-3 col-6">
                    <span className="fw-bold">PCP: </span>
                    {patient.pcps[0]?.name}
                </div>
                <div className="col-md-3 col-6">
                    <span className="fw-bold">Clinic: </span>
                    {patient.pcps[0]?.clinic.name}
                </div>
                <div className="col-md-3 col-6">
                    <span className="fw-bold">Plan Code: </span>
                    {patient.insurance_plans?.[0]?.code}
                    <LitePlanIndicator patient={patient} />
                </div>
                <div className="col-md-3 col-6">
                    <span className="fw-bold">Last OV: </span>
                    {patient.patient_record.last_office_visit
                        ? formatDate(patient.patient_record.last_office_visit)
                        : '--'}
                </div>
            </div>
            {(patient.metadata.Is_Advill || patient.metadata.Is_Frailty) && (
                <div className="row">
                    {patient.metadata.Is_Advill && (
                        <div className="col-md-6 col-6">
                            <span className="fw-bold">Advanced Illness: </span>
                            {patient.metadata.Advill_Prp_Display}
                        </div>
                    )}
                    {patient.metadata.Is_Frailty && (
                        <div className="col-md-6 col-6">
                            <span className="fw-bold">Frailty: </span>
                            {patient.metadata.Frailty_Prp_Display}
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}
